import { Typography, Card, Form, Input, Select, Button, Table, Divider, Tag, message } from 'antd';
import { LeftOutlined, RightOutlined, SendOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getNotifications, sendNotification } from '@/services/notifications';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface Notification { id: string; title: string; body: string; type: string; createdAt: string; recipientCount?: number; }
interface ApiResult { data: { data: Notification[]; meta: { cursor: string; hasMore: boolean } } }

const PAGE_SIZE = 20;

export default function NotificationsPage() {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);
  const page = cursorHistory.length + 1;
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', cursor],
    queryFn: () => getNotifications({ cursor, limit: PAGE_SIZE }) as Promise<ApiResult>,
  });

  const sendMutation = useMutation({
    mutationFn: sendNotification,
    onSuccess: () => {
      message.success('Notification sent successfully');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      form.resetFields();
      setCursor(undefined);
      setCursorHistory([]);
    },
    onError: () => message.error('Failed to send notification'),
  });

  const notifications: Notification[] = (data as ApiResult)?.data?.data ?? [];
  const meta = (data as ApiResult)?.data?.meta;
  const total = (meta as Record<string, unknown>)?.total as number ?? 0;

  function handleNext() { setCursorHistory((h) => [...h, cursor]); setCursor(meta?.cursor); }
  function handlePrev() { const h = [...cursorHistory]; const prev = h.pop(); setCursorHistory(h); setCursor(prev); }

  const columns = [
    {
      title: 'Title', dataIndex: 'title', key: 'title', width: 200,
      render: (v: string) => <span style={{ fontWeight: 600, color: '#0f172a' }}>{v}</span>,
    },
    {
      title: 'Body', dataIndex: 'body', key: 'body',
      render: (v: string) => <span style={{ color: '#64748b' }}>{v?.length > 60 ? v.slice(0, 60) + '…' : v}</span>,
    },
    {
      title: 'Type', dataIndex: 'type', key: 'type', width: 110,
      render: (v: string) => <Tag style={{ borderRadius: 20, fontWeight: 600, fontSize: 11 }}>{v}</Tag>,
    },
    {
      title: 'Recipients', dataIndex: 'recipientCount', key: 'recipientCount', width: 110,
      render: (v: number | undefined) => (
        <span style={{ color: '#0f172a', fontWeight: 600, fontSize: 13 }}>
          {typeof v === 'number' ? v.toLocaleString() : '—'}
        </span>
      ),
    },
    {
      title: 'Sent At', dataIndex: 'createdAt', key: 'createdAt', width: 160,
      render: (v: string) => <span style={{ color: '#64748b', fontSize: 12 }}>{dayjs(v).format('DD MMM YYYY HH:mm')}</span>,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>Notifications</Typography.Title>
        <Typography.Text style={{ color: '#64748b', fontSize: 13 }}>Send push notifications and view history</Typography.Text>
      </div>

      <Card title={<span style={{ fontWeight: 700 }}>Send Notification</span>} style={{ marginBottom: 24, border: '1px solid #e2e8f0', borderRadius: 12 }}>
        <Form form={form} layout="vertical" onFinish={(values) => sendMutation.mutate(values)} style={{ maxWidth: 600 }}>
          <Form.Item name="title" label="Title" rules={[{ required: true, max: 100 }]}>
            <Input placeholder="Notification title" />
          </Form.Item>
          <Form.Item name="body" label="Body" rules={[{ required: true, max: 500 }]}>
            <Input.TextArea rows={3} placeholder="Notification message…" />
          </Form.Item>
          <Form.Item name="targetRole" label="Target audience" rules={[{ required: true }]}>
            <Select placeholder="Who should receive this?" options={[
              { value: 'ALL', label: 'All Users' },
              { value: 'TEACHER', label: 'Teachers only' },
              { value: 'STUDENT', label: 'Students only' },
            ]} />
          </Form.Item>
          <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={sendMutation.isPending}>
            Send Notification
          </Button>
        </Form>
      </Card>

      <Divider style={{ margin: '0 0 20px' }} />

      <div style={{ marginBottom: 16 }}>
        <Typography.Text strong style={{ fontSize: 14, color: '#0f172a' }}>Notification History</Typography.Text>
      </div>

      {isMobile ? (
        /* ── Mobile card list ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isLoading ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
          ) : notifications.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>No notifications sent yet</div>
          ) : notifications.map((n) => (
            <div key={n.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 14, flex: 1 }}>{n.title}</span>
                <Tag style={{ borderRadius: 20, fontWeight: 600, fontSize: 11, margin: 0, flexShrink: 0 }}>{n.type}</Tag>
              </div>
              <div style={{ color: '#64748b', fontSize: 13, marginBottom: 6 }}>
                {n.body?.length > 80 ? n.body.slice(0, 80) + '…' : n.body}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#94a3b8', fontSize: 12 }}>{dayjs(n.createdAt).format('DD MMM YYYY HH:mm')}</span>
                {typeof n.recipientCount === 'number' && (
                  <span style={{ color: '#0f172a', fontSize: 12, fontWeight: 600 }}>
                    {n.recipientCount.toLocaleString()} recipients
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Desktop table ── */
        <Table
          columns={columns}
          dataSource={notifications}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}
        />
      )}

      <div className="cw-pagination">
        <span className="cw-page-info">Page {page}{total > 0 && <> &middot; {total.toLocaleString()} notification{total !== 1 ? 's' : ''}</>}</span>
        <Button icon={<LeftOutlined />} onClick={handlePrev} disabled={cursorHistory.length === 0}>Previous</Button>
        <Button icon={<RightOutlined />} onClick={handleNext} disabled={!meta?.hasMore}>Next</Button>
      </div>
    </div>
  );
}
